import { createSelector } from 'reselect';
import { addDays, isAfter, isBefore, isEqual, isSameDay, isWithinInterval } from 'date-fns/esm';

import { RootState } from '../state';
import { BookingStatus } from '../../enums/BookingStatus';
import Booking from '../../types/model/Booking';
import { RestErrorResponse } from '../../types/response/RestErrorResponse';
import { StateStatus } from '../../enums/StateStatus';
import { selectProjectSettings } from '../bookingProjectSettings/selectors';
import { defaultBookingFilters } from './types';

export const getSelectedDate = (state: RootState) => state.bookings.selectedDate;

export const getBookings = (state: RootState) => state.bookings.byId;

export const getSelectedProjectId = (state: RootState) => state.bookings.selectedProjectId;

export const getFilters = (state: RootState) => state.bookings.filters ?? defaultBookingFilters;

export const getStateStatus = (state: RootState) => state.bookings.status;

export const selectBookings = createSelector([getBookings, getSelectedProjectId], (bookingsById, projectId): Booking[] => {
  return Object.values(bookingsById).filter((booking) => booking.projectId === projectId);
});

export const filterBookingBySearch = (booking: Booking, searchText: string) => {
  const searchTextLower = searchText.toLowerCase();
  if (booking.title) {
    return booking.title.toLowerCase().includes(searchTextLower);
  }
  if (booking.description) {
    return booking.description.toLowerCase().includes(searchText);
  }
  return booking;
};

export const selectFilteredDeliveryZones = createSelector([getFilters, selectProjectSettings], (filters, projectSettings) => {
  const { deliveryZones: filteredDeliveryZones } = filters;
  const { projectDeliveryZones } = projectSettings;

  if (projectDeliveryZones === null || Object.values(projectDeliveryZones).every((el) => el === undefined)) {
    return [];
  }
  return projectDeliveryZones.filter((deliveryZone) => filteredDeliveryZones.includes(deliveryZone.creationIndex));
});

export const selectFilteredBookingsForDate = createSelector(
  [selectBookings, selectFilteredDeliveryZones, getFilters, (_, date: Date) => date],
  (bookings, enabledDeliveryZones, filters, date) => {
    return bookings.filter((booking) => {
      const enabledDeliveryZoneIds = enabledDeliveryZones.map((deliveryZone) => deliveryZone.id);

      return (
        isSameDay(new Date(booking.date), date) &&
        enabledDeliveryZoneIds.includes(booking.deliveryZoneId) &&
        filters.statuses
          .filter((status) => status !== BookingStatus.Canceled && status !== BookingStatus.Rejected)
          .includes(booking.confirmationStatus) &&
        filterBookingBySearch(booking, filters.searchText)
      );
    });
  }
);

export const selectFilteredBookingsForRange = createSelector(
  [selectBookings, selectFilteredDeliveryZones, getFilters, (_, date: Date) => date],
  (bookings, enabledDeliveryZones, filters, date) => {
    const endDate = addDays(date, 5);
    return bookings.filter((booking) => {
      const currentDate = new Date(booking.date);
      const enabledDeliveryZoneIds = enabledDeliveryZones.map((deliveryZone) => deliveryZone.id);

      return (
        (isAfter(currentDate, date) || isEqual(currentDate, date)) &&
        (isBefore(currentDate, endDate) || isEqual(currentDate, endDate)) &&
        enabledDeliveryZoneIds.includes(booking.deliveryZoneId) &&
        filters.statuses
          .filter((status) => status !== BookingStatus.Canceled && status !== BookingStatus.Rejected)
          .includes(booking.confirmationStatus) &&
        filterBookingBySearch(booking, filters.searchText)
      );
    });
  }
);

export const selectFilteredBookings = createSelector(
  [selectBookings, selectFilteredDeliveryZones, getFilters],
  (bookings, enabledDeliveryZones, filters) => {
    const enabledDeliveryZoneIds = enabledDeliveryZones.map((deliveryZone) => deliveryZone.id);

    return bookings.filter((booking) => {
      return (
        isWithinInterval(new Date(booking.date), filters.dateRange) &&
        enabledDeliveryZoneIds.includes(booking.deliveryZoneId) &&
        filters.statuses.includes(booking.confirmationStatus) &&
        filterBookingBySearch(booking, filters.searchText) &&
        (booking.isOwner === true || booking.isOwner === null)
      );
    });
  }
);

export const selectErrorMessage = createSelector(
  [getStateStatus, (state: RootState) => state.bookings.error],
  (status: StateStatus, error: RestErrorResponse | null) => {
    if (status === StateStatus.Error && error) {
      return error.data?.result?.errorMessage ?? null;
    }
    return null;
  }
);
